<template>
  <v-card>
    <v-card-title>Reward Points</v-card-title>
    <div>
      <v-card-text>
        <v-row class="mb-4">
          <!-- Search -->
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-text-field
              v-model="search"
              :append-icon="icons.mdiMagnify"
              label="Search"
              single-line
              hide-details
              dense
              outlined
              @keyup="searchData"
            ></v-text-field>
          </v-col>

          <!-- Start Date -->
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-menu
              v-model="startMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                @input="startMenu = false; filterChanged()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- End Date -->
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-menu
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                @input="endMenu = false; filterChanged()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Store -->
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-select
              v-model="storeCode"
              :items="storeOptions"
              item-text="name"
              item-value="id"
              label="Store"
              outlined
              dense
              hide-details
              @change="filterChanged"
            ></v-select>
          </v-col>

          <!-- Points Target -->
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="pointsTargetFilter"
              :items="pointsTargetOptions"
              label="Points Target"
              outlined
              dense
              hide-details
              @change="filterChanged"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Export CSV Row -->
        <v-row class="mb-4" justify="end">
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-btn
              color="primary"
              block
              :loading="isExporting"
              @click="exportCsv"
            >
              <v-icon class="mr-1">{{ icons.mdiDownload }}</v-icon> Export
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="isLoading"
        class="table-kitchen-sink"
      >
        <!-- Custom empty state -->
        <template v-slot:no-data>
          <div class="text-center py-4">No customers found matching the filters.</div>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import { mdiMagnify, mdiCalendar, mdiDownload } from '@mdi/js'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
	name: 'PointsList',
	data() {
		return {
			startMenu: false,
			endMenu: false,
			isLoading: false,
			isExporting: false,
			headers: [
				{ text: 'CUSTOMER ID', value: 'customer_id' },
				{ text: 'CUSTOMER NAME', value: 'customer_name' },
				{ text: 'PHONE NUMBER', value: 'phone_number' },
				{ text: 'TRANSACTION COUNT', value: 'transaction_count' },
				{ text: 'TOTAL POINTS', value: 'total_points' },
				{ text: 'TOTAL AMOUNT SPENT', value: 'total_amount_spent' },
			],
			icons: {
				mdiMagnify,
				mdiCalendar,
				mdiDownload,
			},
		}
	},
	computed: {
		...mapGetters('points', ['items', 'totalItems']),
		options: {
			get() {
				return this.$store.getters['points/options']
			},
			set(value) {
				this.$store.commit('points/SET_OPTIONS', value)
			},
		},
		search: {
			get() {
				return this.$store.getters['points/search']
			},
			set(value) {
				this.$store.commit('points/SET_SEARCH', value)
			},
		},
		startDate: {
			get() {
				return this.$store.getters['points/startDate']
			},
			set(value) {
				this.$store.commit('points/SET_START_DATE', value)
			},
		},
		endDate: {
			get() {
				return this.$store.getters['points/endDate']
			},
			set(value) {
				this.$store.commit('points/SET_END_DATE', value)
			},
		},
		storeCode: {
			get() {
				return this.$store.getters['points/storeCode']
			},
			set(value) {
				this.$store.commit('points/SET_STORE_CODE', value)
			},
		},
		pointsTargetFilter: {
			get() {
				return this.$store.getters['points/pointsTargetFilter']
			},
			set(value) {
				this.$store.commit('points/SET_POINTS_TARGET_FILTER', value)
			},
		},
		outlets() {
			return this.$store.getters['outlet/outlets'] || []
		},
		storeOptions() {
			return [{ name: 'All Stores', id: '' }, ...this.outlets]
		},
		weeklyPoint() {
			return this.$store.state.settings.settings?.weekly_point || 5000
		},
		pointsTargetOptions() {
			return [
				{ text: `${this.weeklyPoint} and Above`, value: 'above' },
				{ text: `Below ${this.weeklyPoint}`, value: 'below' },
			]
		},
	},
	watch: {
		options() {
			this.datatable()
		},
	},
	mounted() {
		this.$store.dispatch('outlet/all')
		this.$store.dispatch('settings/get')
		this.datatable()
	},
	methods: {
		searchData: _.debounce(function fn() {
			this.options = { ...this.options, page: 1 }
			this.datatable()
		}, 400),
		filterChanged() {
			this.options = { ...this.options, page: 1 }
			this.datatable()
		},
		datatable() {
			this.isLoading = true
			this.$store
				.dispatch('points/datatable')
				.catch(error => {
					this.$store.commit('app/SET_SNACKBAR_ERROR', error.response?.data?.message || 'Failed to load points data.')
				})
				.finally(() => {
					this.isLoading = false
				})
		},
		exportCsv() {
			this.isExporting = true
			this.$store
				.dispatch('points/exportData')
				.catch(error => {
					this.$store.commit('app/SET_SNACKBAR_ERROR', error.response?.data?.message || 'Failed to export CSV.')
				})
				.finally(() => {
					this.isExporting = false
				})
		},
	},
}
</script>

<style scoped>
</style>
